import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import moment from 'moment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import { AI_SEARCH_PARAM_KEYS } from 'modules/shift_management/lib/aiSearchParams'
import View, {
  DEFAULT_GROUP_TYPE,
  DEFAULT_VIEW_TYPE,
  type GroupType,
  type ViewType,
  isValidGroupType,
  isValidViewType,
} from 'modules/shift_management/store/internal/models/View'

type Data = {
  date: string
  locationId?: string
  teamId?: string
  employeeIds?: string
}

/**
 * Headless activity: navigates to the correct date on the shift management page
 * and passes AI-driven filters via search params (_ai_employees, _ai_location, _ai_team).
 * FiltersProvider reacts to these search param changes and applies them.
 *
 * We use React Router's navigate() instead of window.history.replaceState() so the
 * router-managed location/search state updates. This does not rely on the route or
 * FiltersProvider re-mounting: the existing FiltersProvider stays mounted and reacts
 * to the updated _ai_* params via its searchParams effect.
 *
 * Params are strings because One currently sends activity params as strings.
 * - date: YYYY-MM-DD
 * - locationId: optional single location ID
 * - teamId: optional single team ID
 * - employeeIds: optional comma-separated employee IDs
 */
export const FilterEmployeeListActivity: ActivityComponent<Data> = ({
  date,
  locationId,
  teamId,
  employeeIds,
}) => {
  // closeActivity is intentionally not called — it navigates with a stale `search` snapshot
  // that would overwrite the _ai_* params we set below. Navigating without a hash implicitly
  // closes the activity (the hash encodes the activity id; clearing it closes it).
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const params = useParams()

  useEffect(() => {
    const parsedDate = moment(date, 'YYYY-MM-DD', true)
    const targetDate = parsedDate.isValid() ? parsedDate : moment()

    const viewTypeParam = params.viewType
    const groupTypeParam = params.groupType
    const viewType: ViewType = isValidViewType(viewTypeParam) ? viewTypeParam : DEFAULT_VIEW_TYPE
    const groupType: GroupType = isValidGroupType(groupTypeParam)
      ? groupTypeParam
      : DEFAULT_GROUP_TYPE

    const view = new View({ date: targetDate, viewType, groupType, isTimeFormat24H: false })

    const path = navigation.shifts.customViewGroup.fullPath({
      viewType: view.viewType,
      groupType: view.groupType,
      year: view.startRangeYear,
      month: view.startRangeMonth,
      day: view.startRangeDay,
    })

    const searchParams = new URLSearchParams()
    if (employeeIds) searchParams.set(AI_SEARCH_PARAM_KEYS.employees, employeeIds)
    if (locationId) searchParams.set(AI_SEARCH_PARAM_KEYS.location, locationId)
    if (teamId) searchParams.set(AI_SEARCH_PARAM_KEYS.team, teamId)

    const search = searchParams.toString()
    // Navigate without a hash to close the activity and set AI filter params.
    navigate(`${path}${search ? `?${search}` : ''}`, { replace: true })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return null
}
